// autopilot-doctor-setting-sources-checks.ts
//
// 06g R4 (security audit 2026-10-12 H1): workers start with
// `--setting-sources project` (claude-headless-argv-builder.ts), so
// (1) a `claude` binary without that flag cannot run a worker at all — a
//     `fail` here replaces a minimum-version check (QA NST);
// (2) keys in `~/.claude/settings.json` the worker used to inherit —
//     `permissions.allow` with Bash/WebFetch/mcp__, `hooks`, `env`,
//     `apiKeyHelper` — are no longer loaded; a dev using a gateway through
//     user settings must declare it in the mission's own settings. `warn`,
//     never `fail`: nothing is broken, the operator just has to know.
// Pure check over already-read inputs + the two tiny I/O probes. The help
// probe runs the SAME binary the worker spawn resolves (review 06g M3).
import { promises as fs } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";
import type { DoctorCheck } from "./autopilot-environment-checks.js";
import { WORKER_SETTING_SOURCES } from "./claude-headless-argv-builder.js";
import { resolveClaudeBin } from "./run-command-session-deps.js";

export interface SettingSourcesDoctorInput {
  /** `claude --help` stdout, or `(unavailable)` when the binary cannot be spawned. */
  helpText: string;
  /** Parsed `~/.claude/settings.json`; undefined when absent/unreadable. */
  userSettings: unknown;
}

const SETTING_SOURCES_FLAG = "--setting-sources";
const DROPPED_ALLOW_PREFIXES = ["Bash(", "WebFetch", "mcp__"] as const;
const DROPPED_TOP_LEVEL_KEYS = ["hooks", "env", "apiKeyHelper"] as const;

/** Names of user-level keys the worker will not load — names only, never values (a gateway URL or helper path is the dev's). */
function droppedUserSettingKeys(userSettings: unknown): string[] {
  if (userSettings === null || typeof userSettings !== "object") return [];
  const settings = userSettings as Record<string, unknown>;
  const dropped: string[] = [];
  const allow = (settings.permissions as { allow?: unknown } | undefined)?.allow;
  if (Array.isArray(allow)) {
    const wide = allow
      .filter((rule): rule is string => typeof rule === "string")
      .filter((rule) => DROPPED_ALLOW_PREFIXES.some((prefix) => rule.startsWith(prefix)));
    if (wide.length > 0) dropped.push(`permissions.allow (${wide.join(", ")})`);
  }
  for (const key of DROPPED_TOP_LEVEL_KEYS) if (settings[key] !== undefined) dropped.push(key);
  return dropped;
}

export function settingSourcesDoctorChecks(input: SettingSourcesDoctorInput): DoctorCheck[] {
  const checks: DoctorCheck[] = [];
  // Binary absent ⇒ the `claude` check already fails; do not report it twice.
  if (input.helpText !== "(unavailable)") {
    const hasFlag = input.helpText.includes(SETTING_SOURCES_FLAG);
    checks.push({
      name: "claude --setting-sources",
      status: hasFlag ? "ok" : "fail",
      detail: hasFlag
        ? `worker chạy với --setting-sources ${WORKER_SETTING_SOURCES} (bỏ settings user/local)`
        : `'claude --help' không có ${SETTING_SOURCES_FLAG} — binary quá cũ cho worker (cần bản có cờ này; đo trên 2.1.293)`,
    });
  }
  const dropped = droppedUserSettingKeys(input.userSettings);
  if (dropped.length > 0) {
    checks.push({
      name: "claude user settings",
      status: "warn",
      detail:
        `~/.claude/settings.json có ${dropped.join("; ")} — các khoá này KHÔNG nạp vào worker ` +
        `(--setting-sources ${WORKER_SETTING_SOURCES}); gateway/apiKeyHelper cần khai trong settings của mission.`,
    });
  }
  return checks;
}

/** `<bin> --help` stdout — `(unavailable)` on any spawn failure (same contract as claude-version-probe.ts). */
export async function probeClaudeHelpText(bin: string = resolveClaudeBin()): Promise<string> {
  try {
    const res = await spawnAsyncCapture(bin, ["--help"], { timeoutMs: 10_000 });
    return res.status === 0 ? res.stdout : "(unavailable)";
  } catch {
    return "(unavailable)";
  }
}

/** Parsed `~/.claude/settings.json` (the ONE user-level settings file) — undefined when absent or not JSON. */
export async function readUserClaudeSettings(home: string = homedir()): Promise<unknown> {
  try {
    return JSON.parse(await fs.readFile(join(home, ".claude", "settings.json"), "utf8"));
  } catch {
    return undefined;
  }
}

export async function probeSettingSourcesDoctorInput(
  env: NodeJS.ProcessEnv = process.env,
): Promise<SettingSourcesDoctorInput> {
  const [helpText, userSettings] = await Promise.all([
    probeClaudeHelpText(resolveClaudeBin(env)),
    readUserClaudeSettings(env.HOME || homedir()),
  ]);
  return { helpText, userSettings };
}
